import { useEffect, useState } from 'react';
import {
	Modal,
	Form,
	Button,
	Select,
	InputNumber,
	Row,
	Col,
	Splitter,
	Alert,
	Tabs,
	Typography,
	Empty,
	Tooltip,
	Spin,
	Checkbox,
	message,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
	useGetMaterialShapesQuery,
	useGetMaterialTypesQuery,
	useGetMaterialCategoriesQuery,
	useCalculateRequiredLengthsMutation,
	useGetMaterialsQuery,
	useLazyGetMaterialByIdQuery,
} from '../../redux/api/material';
import { useGetBuildingsQuery } from '../../redux/api/location';
import {
	ANGLE,
	CHANNEL,
	PLATE,
	RECTANGLE_BAR,
	RECTANGLE_TUBE,
	ROUND_BAR,
	ROUND_TUBE,
	HEX_BAR,
} from '../../config/constants';
import { showMaterialRequestModal } from '../../redux/slices/material-request';
import { Lineitem, ProductionMethod } from '../../interfaces/Ordel.Interface';
import { DEFAULT_SAW_KERF, SWISS_SAW_KERF, DEFAULT_TRIM_CUT_LENGTH } from '../../utils';
import ThreeDStockSizePreviewer from '../ThreeDStockPreviewer/lazy.tsx';
import {
	useGetUniqueInventoryMaterialHeightsQuery,
	useGetUniqueInventoryMaterialInnerDiametersQuery,
	useGetUniqueInventoryMaterialOuterDiametersQuery,
	useGetUniqueInventoryMaterialWallThicknessesQuery,
	useGetUniqueInventoryMaterialWidthsQuery,
} from '../../redux/api/material';
import {
	MaterialRequest,
	MaterialShape,
} from '../../interfaces/Material.Interface';
import {
	getGeometryErrorMessage,
	useAlignPartByFaceMutation,
	useAlignPartToXAxisByFaceMutation,
	useGetStockPreviewQuery,
	useGetSuggestedInventoryMaterialOnPartOrientationQuery,
	useRotatePartMutation,
} from '../../redux/api/order-line-item';
import {
	LoadingOutlined,
	ReloadOutlined,
	QuestionCircleTwoTone,
} from '@ant-design/icons';
import { RootState } from '../../redux/store';
import MaterialInventoryTable from '../MaterialInventoryTable';
import { closeMaterialSelectionModal } from '../../redux/slices/material-selection';
import { Material } from '../../interfaces/MaterialInventory.interface';
import { getEffectiveShapeName } from '../../utils/shapeUtils';

const { Text, Title } = Typography;

interface MaterialModalProps {
	lineItem: Lineitem;
	materialRequest?: MaterialRequest | null;
	refetchLineItem: () => void;
	onMaterialSelect?: (material: Material, refetch: any) => void;
}

const MaterialSelectionModal2 = (props: MaterialModalProps) => {
	const dispatch = useDispatch();

	const visible = useSelector(
		(state: RootState) => state.materialSelection.materialSelectionModalVisible
	);

	const isSwissProduction =
		props.lineItem.partsbadger_production_method === ProductionMethod.Swiss;

	const [form] = Form.useForm();

	const [allowRotatedStock, setAllowRotatedStock] = useState(false);

	const [loading, setLoading] = useState(false);

	const [selectedShape, setSelectedShape] = useState<MaterialShape | null>(
		null
	);
	const normalizeShape = (
		shape: MaterialShape | null | undefined
	): MaterialShape | null => {
		if (!shape) return null;
		return {
			...shape,
			display_name:
				getEffectiveShapeName(shape.display_name) ?? shape.display_name,
		};
	};
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
	const [selectedTypes, setSelectedTypes] = useState<string | string[] | []>(
		[]
	);
	const [selectedHeight, setSelectedHeight] = useState<string | null>(null);
	const [selectedWidth, setSelectedWidth] = useState<string | null>(null);
	const [selectedOuterDiameter, setSelectedOuterDiameter] = useState<
		string | null
	>(null);
	const [selectedInnerDiameter, setSelectedInnerDiameter] = useState<
		string | null
	>(null);
	const [selectedWallThickness, setSelectedWallThickness] = useState<
		string | null
	>(null);
	const [materialCutLength, setMaterialCutLength] = useState<string | null>(
		null
	);
	const [sawKerf, setSawKerf] = useState<number>(
		isSwissProduction ? SWISS_SAW_KERF : DEFAULT_SAW_KERF
	);
	const [cutQuantity, setCutQuantity] = useState<number>(
		props.lineItem.quantity
	);
	const [estimatedScrapQuantity, setEstimatedScrapQuantity] = useState<number>(
		isSwissProduction
			? 10 + Math.round(props.lineItem.quantity * 0.03)
			: Math.round(props.lineItem.quantity * 0.15)
	);
	const [trimCutLength, setTrimCutLength] = useState<number>(DEFAULT_TRIM_CUT_LENGTH);
	const [totalRequiredLength, setTotalRequiredLength] = useState<number>(0);
	const [numberOfBars, setNumberOfBars] = useState<number>(0);
	const [autoCalculatedPartLength, setAutoCalculatedPartLength] = useState<
		string | null
	>(null);

	const [selectedMaterial, setSelectedMaterial] = useState<Material | null>();
	const [selectedBuilding, setSelectedBuilding] = useState<string | null>(null);

	const { data: buildings = [] } = useGetBuildingsQuery();
	const showBuildingFilter = buildings.length > 1;
	const lockedBuilding = props.lineItem.locked_building ?? null;
	const isBuildingLocked = lockedBuilding !== null;

	useEffect(() => {
		if (isBuildingLocked && visible) {
			setSelectedBuilding(lockedBuilding.id);
		}
	}, [isBuildingLocked, lockedBuilding?.id, visible]);

	const shapesQuery = useGetMaterialShapesQuery();
	const categoriesQuery = useGetMaterialCategoriesQuery();
	const typesQuery = useGetMaterialTypesQuery(
		{ material_category: selectedCategory },
		{ refetchOnMountOrArgChange: true }
	);

	const getSuggestedInventoryMaterialBasedOnPartOrientationQuery =
		useGetSuggestedInventoryMaterialOnPartOrientationQuery(
			{
				id: props.lineItem.id,
				shape: selectedShape?.id,
				types: selectedTypes,
			},
			{
				skip:
					!props?.lineItem ||
					!selectedShape ||
					selectedTypes.length === 0 ||
					(selectedShape?.display_name !== RECTANGLE_BAR &&
						selectedShape?.display_name !== ROUND_BAR),
				refetchOnMountOrArgChange: true,
			}
		);

	const uniqueHeightsQuery = useGetUniqueInventoryMaterialHeightsQuery(
		{
			shape: selectedShape?.id || '',
			...(selectedCategory ? { category: selectedCategory } : null),
			...(selectedTypes ? { types: selectedTypes } : null),
		} as any,
		{
			skip: !selectedShape,
			refetchOnMountOrArgChange: true,
		}
	);

	const uniqueWidthsQuery = useGetUniqueInventoryMaterialWidthsQuery(
		{
			shape: selectedShape?.id || '',
			...(selectedCategory ? { category: selectedCategory } : null),
			...(selectedTypes ? { types: selectedTypes } : null),
			...(selectedHeight ? { height: selectedHeight } : null),
		} as any,
		{
			skip: !selectedShape || !selectedHeight,
			refetchOnMountOrArgChange: true,
		}
	);
	const uniqueOuterDiametersQuery =
		useGetUniqueInventoryMaterialOuterDiametersQuery(
			{
				shape: selectedShape?.id || '',
				...(selectedCategory ? { category: selectedCategory } : null),
				...(selectedTypes ? { types: selectedTypes } : null),
			} as any,
			{
				skip: !selectedShape,
				refetchOnMountOrArgChange: true,
			}
		);
	const uniqueInnerDiametersQuery =
		useGetUniqueInventoryMaterialInnerDiametersQuery(
			{
				shape: selectedShape?.id || '',
				...(selectedCategory ? { category: selectedCategory } : null),
				...(selectedTypes ? { types: selectedTypes } : null),
				...(selectedOuterDiameter
					? { outer_diameter: selectedOuterDiameter }
					: null),
			} as any,
			{
				skip: !selectedShape,
				refetchOnMountOrArgChange: true,
			}
		);
	const uniqueWallThicknessesQuery =
		useGetUniqueInventoryMaterialWallThicknessesQuery(
			{
				shape: selectedShape?.id || '',
				...(selectedCategory ? { category: selectedCategory } : null),
				...(selectedTypes ? { types: selectedTypes } : null),
				...(selectedHeight ? { height: selectedHeight } : null),
				...(selectedWidth ? { width: selectedWidth } : null),
			} as any,
			{
				skip: !selectedShape,
				refetchOnMountOrArgChange: true,
			}
		);

	const stockPreviewQuery = useGetStockPreviewQuery(
		{
			id: props.lineItem.id,
			params: {
				type: 'inventory_material',
				material_id: selectedMaterial?.id,
				length: materialCutLength,
				stock_facing_amount: 0.03,
				is_stock_rotated: allowRotatedStock,
				width: selectedWidth,
				height: selectedHeight,
			},
		},
		{ skip: !visible, refetchOnMountOrArgChange: true }
	);

	const [rotatePart, rotatePartResult] = useRotatePartMutation();
	const [alignByFace, alignByFaceResult] = useAlignPartByFaceMutation();
	const [alignToXAxisByFace, alignToXAxisByFaceResult] =
		useAlignPartToXAxisByFaceMutation();

	// Async OCC geometry ops (PTB-6193): these mutations' promises stay pending
	// for the whole POST + poll round trip (a few seconds against the SFA, not
	// the near-instant sync calls this used to be), so any UI that lets the
	// user re-trigger a rotate/align must stay disabled for this whole window.
	const isGeometryOpPending =
		rotatePartResult.isLoading ||
		alignByFaceResult.isLoading ||
		alignToXAxisByFaceResult.isLoading;

	const [calculateRequiredLengths, calculateRequiredLengthsResult] =
		useCalculateRequiredLengthsMutation();

	const materialsQuery = useGetMaterialsQuery(
		{
			...(getSuggestedInventoryMaterialBasedOnPartOrientationQuery.isSuccess
				? {
					// Remove search for now, due to problem for now
					searc:
						getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data
							?.material?.id,
				}
				: null),
			shape: selectedShape?.id,
			category: selectedCategory || undefined,
			types: selectedTypes,
			min_total_available_length: 0.01,
			...(selectedHeight ? { height: selectedHeight } : null),
			...(selectedOuterDiameter
				? { outer_diameter: selectedOuterDiameter }
				: null),
			...(selectedWidth ? { width: selectedWidth } : null),
			...(selectedBuilding ? { building: selectedBuilding } : null),
		},
		{
			skip: !selectedShape || !selectedCategory || selectedTypes.length === 0 || (showBuildingFilter && !selectedBuilding),
			refetchOnMountOrArgChange: true,
		}
	);

	const [getMaterial] = useLazyGetMaterialByIdQuery();

	async function handleResetPartRotation() {
		await rotatePart({
			id: props.lineItem.id,
			body: {
				part_transform_matrix: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
			},
		})
			.unwrap()
			.then(() => props.refetchLineItem());
	}

	async function handleClickFaceForAlignment(faceId: number) {
		await alignByFace({
			id: props.lineItem.id,
			body: {
				id: faceId,
			},
		})
			.unwrap()
			.then(() => props.refetchLineItem());
	}

	async function handleClickFaceForXAxisAlignment(faceId: number) {
		await alignToXAxisByFace({
			id: props.lineItem.id,
			body: {
				id: faceId,
			},
		})
			.unwrap()
			.then(() => props.refetchLineItem());
	}

	async function updateMatrix(matrix: number[]) {
		await rotatePart({
			id: props.lineItem.id,
			body: {
				part_transform_matrix: matrix,
			},
		})
			.unwrap()
			.then(() => props.refetchLineItem());
	}

	const calculateSwissRequiredLength = (
		partLength: number,
		cutOffLength: number,
		parts: number,
		estimatedScrap: number
	) => {
		const totalInchesNeeded =
			(partLength + cutOffLength) * (parts + estimatedScrap) * (12 / 11);
		const roundedTotalInchesNeeded = Number(totalInchesNeeded.toFixed(2));
		const barsNeeded = Math.ceil(totalInchesNeeded / 144);
		return { totalInchesNeeded: roundedTotalInchesNeeded, barsNeeded };
	};

	function onClose() {
		dispatch(closeMaterialSelectionModal());
		setSelectedShape(null);
		setSelectedCategory(null);
		setSelectedTypes([]);
		setSelectedHeight(null);
		setSelectedWidth(null);
		setSelectedOuterDiameter(null);
		setSelectedInnerDiameter(null);
		setSelectedWallThickness(null);
		setSelectedBuilding(null);
		setTotalRequiredLength(0);
		setNumberOfBars(0);
		setMaterialCutLength(null);
		setAutoCalculatedPartLength(null);
		setSawKerf(isSwissProduction ? SWISS_SAW_KERF : DEFAULT_SAW_KERF);
		setCutQuantity(props.lineItem.quantity);
		setEstimatedScrapQuantity(
			isSwissProduction
				? 10 + Math.round(props.lineItem.quantity * 0.03)
				: Math.round(props.lineItem.quantity * 0.15)
		);
		setTrimCutLength(DEFAULT_TRIM_CUT_LENGTH);
	}

	const handleCancel = () => {
		onClose();
	};

	// Handle shape change
	const handleShapeChange = (value: string) => {
		const shape = shapesQuery?.data?.find((x) => x.id === value);

		if (shape) {
			setSelectedShape(normalizeShape(shape));

			if (shape.display_name === RECTANGLE_BAR) {
				setAllowRotatedStock(true);
			} else {
				setAllowRotatedStock(false);
			}
		}

		setSelectedHeight(null);
		setSelectedWidth(null);
		setSelectedOuterDiameter(null);
		setSelectedInnerDiameter(null);
		setSelectedWallThickness(null);
		setSelectedMaterial(null);
		setTotalRequiredLength(0);
		setNumberOfBars(0);
		setMaterialCutLength(null);
		setAutoCalculatedPartLength(null);
	};

	// Handle shape change
	const handleCategoryChange = (value: string) => {
		setSelectedCategory(value);
	};

	const handleChangeSelectedHeight = (value: string) => {
		setSelectedHeight(value);
		setSelectedMaterial(null);
	};

	const handleChangeSelectedWidth = (value: string) => {
		setSelectedWidth(value);
		setSelectedMaterial(null);
	};

	const handleChangeOuterDiameter = (value: string) => {
		setSelectedOuterDiameter(value);
		setSelectedMaterial(null);
	};

	const handleChangeInnerDiameter = (value: string) => {
		setSelectedInnerDiameter(value);
		setSelectedMaterial(null);
	};

	const handleMaterialCutLengthChange = (value: string | number) => {
		if (!value) return;
		// Round to 2 decimal places
		const roundedValue = String(Number(Number(value).toFixed(2)));

		if (Number(roundedValue) !== Number(materialCutLength)) {
			setMaterialCutLength(roundedValue);

			if (isSwissProduction) {
				const result = calculateSwissRequiredLength(
					Number(roundedValue),
					sawKerf,
					cutQuantity,
					estimatedScrapQuantity
				);
				setTotalRequiredLength(result.totalInchesNeeded);
				setNumberOfBars(result.barsNeeded);
			} else {
				calculateRequiredLengths({
					material_cut_length: Number(roundedValue),
					saw_kerf: sawKerf,
					material_cut_quantity: cutQuantity,
					required_trim_cut_length: trimCutLength,
				})
					.unwrap()
					.then((data) => {
						setTotalRequiredLength(data.total_required_length);
					});
			}
		}
	};

	const handleSawKerfChange = (value: number) => {
		if (Number(value) !== Number(sawKerf)) {
			setSawKerf(value);
			if (isSwissProduction && materialCutLength) {
				const result = calculateSwissRequiredLength(
					Number(materialCutLength),
					value,
					cutQuantity,
					estimatedScrapQuantity
				);
				setTotalRequiredLength(result.totalInchesNeeded);
				setNumberOfBars(result.barsNeeded);
		} else {
			calculateRequiredLengths({
				material_cut_length: Number(materialCutLength),
				saw_kerf: value,
				material_cut_quantity: cutQuantity,
				required_trim_cut_length: trimCutLength,
			})
					.unwrap()
					.then((data) => {
						setTotalRequiredLength(data.total_required_length);
					});
			}
		}
	};

	const handleMaterialCutQuantityChange = (value: number) => {
		if (Number(value) !== Number(cutQuantity)) {
			setCutQuantity(value);

			if (isSwissProduction) {
				const newEstimatedScrapQuantity = 10 + Math.round(value * 0.03);
				setEstimatedScrapQuantity(newEstimatedScrapQuantity);
				if (materialCutLength) {
					const result = calculateSwissRequiredLength(
						Number(materialCutLength),
						sawKerf,
						value,
						newEstimatedScrapQuantity
					);
					setTotalRequiredLength(result.totalInchesNeeded);
					setNumberOfBars(result.barsNeeded);
				}
				return;
			}

			if (materialCutLength) {
				calculateRequiredLengths({
					material_cut_length: Number(materialCutLength),
					saw_kerf: sawKerf,
					material_cut_quantity: value,
					required_trim_cut_length: trimCutLength,
				})
					.unwrap()
					.then((data) => {
						setTotalRequiredLength(data.total_required_length);
					});
			}
		}
	};

	const handleMaterialScrapQuantityChange = (value: number) => {
		if (Number(value) !== Number(estimatedScrapQuantity)) {
			setEstimatedScrapQuantity(value);
			if (isSwissProduction && materialCutLength) {
				const result = calculateSwissRequiredLength(
					Number(materialCutLength),
					sawKerf,
					cutQuantity,
					value
				);
				setTotalRequiredLength(result.totalInchesNeeded);
				setNumberOfBars(result.barsNeeded);
		} else {
			calculateRequiredLengths({
				material_cut_length: Number(materialCutLength),
				saw_kerf: sawKerf,
				material_cut_quantity: cutQuantity + value,
				required_trim_cut_length: trimCutLength,
			})
					.unwrap()
					.then((data) => {
						setTotalRequiredLength(data.total_required_length);
					});
			}
		}
	};

	const handleResetPartLength = () => {
		if (autoCalculatedPartLength) {
			const roundedValue = String(
				Number(Number(autoCalculatedPartLength).toFixed(2))
			);
			setMaterialCutLength(roundedValue);
			form.setFieldValue('material_cut_length', Number(roundedValue));
			handleMaterialCutLengthChange(roundedValue);
		}
	};

	function onClickAutoSelectMaterialSize() {
		if (selectedShape?.display_name === RECTANGLE_BAR) {
			setSelectedHeight(
				String(
					getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data
						?.material?.height
				)
			);

			setSelectedWidth(
				String(
					getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data
						?.material?.width
				)
			);
		} else if (selectedShape?.display_name === ROUND_BAR) {
			setSelectedOuterDiameter(
				String(
					getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data
						?.material?.outer_diameter
				)
			);
		}
		const rawValue =
			getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data
				?.material_cut_length;
		// Round to 2 decimal places
		const autoCalculatedValue = rawValue
			? String(Number(Number(rawValue).toFixed(2)))
			: null;
		setAutoCalculatedPartLength(autoCalculatedValue);
		setMaterialCutLength(autoCalculatedValue);
		if (autoCalculatedValue) {
			form.setFieldValue('material_cut_length', Number(autoCalculatedValue));
			handleMaterialCutLengthChange(autoCalculatedValue);
		}
		setSelectedMaterial(null);
	}

	async function handleChangePreviewMaterial(value: string | null) {
		getMaterial({ id: value })
			.unwrap()
			.then((data) => {
				setSelectedMaterial(data);
			});
	}

	useEffect(() => {
		if (alignByFaceResult.isSuccess) {
			props.refetchLineItem();
			if (!stockPreviewQuery.isUninitialized) {
				stockPreviewQuery.refetch();
			}
			if (
				!getSuggestedInventoryMaterialBasedOnPartOrientationQuery.isUninitialized
			) {
				getSuggestedInventoryMaterialBasedOnPartOrientationQuery.refetch();
			}
		}
	}, [alignByFaceResult]);

	useEffect(() => {
		if (alignToXAxisByFaceResult.isSuccess) {
			props.refetchLineItem();
			if (!stockPreviewQuery.isUninitialized) {
				stockPreviewQuery.refetch();
			}
			if (
				!getSuggestedInventoryMaterialBasedOnPartOrientationQuery.isUninitialized
			) {
				getSuggestedInventoryMaterialBasedOnPartOrientationQuery.refetch();
			}
		}
	}, [alignToXAxisByFaceResult]);

	useEffect(() => {
		if (rotatePartResult.isSuccess) {
			props.refetchLineItem();
			if (!stockPreviewQuery.isUninitialized) {
				stockPreviewQuery.refetch();
			}
			if (
				!getSuggestedInventoryMaterialBasedOnPartOrientationQuery.isUninitialized
			) {
				getSuggestedInventoryMaterialBasedOnPartOrientationQuery.refetch();
			}
		}
	}, [rotatePartResult]);

	// Async OCC geometry ops (PTB-6193): rotate-part/align-* fire a 202 and the
	// mutation's promise doesn't settle until the SFA's result has been polled
	// to "ready" or "failed" -- these effects only need to handle the failed/
	// timed-out case, since success is already handled by the effects above.
	useEffect(() => {
		if (rotatePartResult.isError) {
			message.error(
				getGeometryErrorMessage(
					rotatePartResult.error,
					'Failed to rotate the part. Please try again.'
				)
			);
		}
	}, [rotatePartResult.isError, rotatePartResult.error]);

	useEffect(() => {
		if (alignByFaceResult.isError) {
			message.error(
				getGeometryErrorMessage(
					alignByFaceResult.error,
					'Failed to align the part by the selected face. Please try again.'
				)
			);
		}
	}, [alignByFaceResult.isError, alignByFaceResult.error]);

	useEffect(() => {
		if (alignToXAxisByFaceResult.isError) {
			message.error(
				getGeometryErrorMessage(
					alignToXAxisByFaceResult.error,
					'Failed to align the part to the X-axis. Please try again.'
				)
			);
		}
	}, [alignToXAxisByFaceResult.isError, alignToXAxisByFaceResult.error]);

	useEffect(() => {
		if (stockPreviewQuery.isError) {
			message.error(
				getGeometryErrorMessage(
					stockPreviewQuery.error,
					'Failed to generate the stock preview. Please try again.'
				)
			);
		}
	}, [stockPreviewQuery.isError, stockPreviewQuery.error]);

	useEffect(() => {
		if (getSuggestedInventoryMaterialBasedOnPartOrientationQuery.isError) {
			message.error(
				getGeometryErrorMessage(
					getSuggestedInventoryMaterialBasedOnPartOrientationQuery.error,
					'Failed to search inventory for a suggested material. Please try again.'
				)
			);
		}
	}, [
		getSuggestedInventoryMaterialBasedOnPartOrientationQuery.isError,
		getSuggestedInventoryMaterialBasedOnPartOrientationQuery.error,
	]);

	// Basic function that cheks the name of the material in the order line item
	// and selects material types that match a substring.
	useEffect(() => {
		if (props.lineItem?.material_name && typesQuery.isSuccess) {
			const material = props.lineItem.material_name.toLowerCase();

			const types: string[] = [];
			const categories = new Set<string>();

			// Split "material" into tokens (words)
			const tokens = material
				.split(/\s+/) // split on whitespace
				.filter(Boolean); // remove empty strings

			for (const type of typesQuery.data) {
				const displayNameLower = type.display_name.toLowerCase();

				// Check if the displayName includes **all** tokens
				const hasAllTokens = tokens.every((token) =>
					displayNameLower.includes(token)
				);

				if (hasAllTokens) {
					types.push(type.id);
					if (type.material_category?.id) {
						categories.add(type.material_category.id);
					}
				}
			}

			// If there's exactly one category, set it
			if (categories.size === 1) {
				const category = Array.from(categories)[0];
				handleCategoryChange(category);
				form.setFieldValue('category', category);
			}

			// Update types and query params
			setSelectedTypes([...types]);
			form.setFieldValue('types', [...types]);
		}
	}, [typesQuery]);

	useEffect(() => {
		if (
			visible &&
			shapesQuery.isSuccess &&
			!selectedShape
		) {
			if (isSwissProduction) {
				// Swiss defaults to Round Bar
				const roundBar = shapesQuery?.data?.find(
					(x) => x.display_name === ROUND_BAR
				);
				if (roundBar) {
					setSelectedShape(normalizeShape(roundBar));
					form.setFieldValue('shape', roundBar.id);
				}
			} else {
				// Other production methods default to Rectangle Bar
				const rectangleBar = shapesQuery?.data?.find(
					(x) => x.display_name === RECTANGLE_BAR
				);
				if (rectangleBar) {
					setSelectedShape(normalizeShape(rectangleBar));
					form.setFieldValue('shape', rectangleBar.id);
				}
			}
		}
	}, [visible, shapesQuery, isSwissProduction, selectedShape, form]);

	useEffect(() => {
		if (
			getSuggestedInventoryMaterialBasedOnPartOrientationQuery.isSuccess &&
			getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data
				?.material &&
			(selectedShape?.display_name === RECTANGLE_BAR ||
				selectedShape?.display_name === ROUND_BAR)
		) {
			// Store auto-calculated value for Reset functionality (rounded to 2 decimals)
			const rawValue =
				getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data
					?.material_cut_length;
			const autoCalculatedValue = rawValue
				? String(Number(Number(rawValue).toFixed(2)))
				: null;
			setAutoCalculatedPartLength(autoCalculatedValue);

			onClickAutoSelectMaterialSize();
		}
	}, [getSuggestedInventoryMaterialBasedOnPartOrientationQuery, selectedShape]);

	useEffect(() => {
		if (
			selectedShape &&
			stockPreviewQuery.isSuccess &&
			stockPreviewQuery?.data?.part_bbox &&
			!materialCutLength
		) {
			let length = 0;
			if (
				selectedShape?.display_name === RECTANGLE_BAR ||
				selectedShape?.display_name === RECTANGLE_TUBE ||
				selectedShape?.display_name === HEX_BAR ||
				selectedShape?.display_name === ANGLE ||
				selectedShape?.display_name === CHANNEL ||
				selectedShape?.display_name === PLATE
			) {
				length = stockPreviewQuery?.data?.part_bbox?.length + 0.25;
			} else if (
				selectedShape?.display_name === ROUND_BAR ||
				selectedShape?.display_name === ROUND_TUBE
			) {
				length = stockPreviewQuery?.data?.part_bbox.height + 0.25;
			}

			if (length > 0) {
				setMaterialCutLength(String(length));

				if (isSwissProduction) {
					const result = calculateSwissRequiredLength(
						length,
						sawKerf,
						cutQuantity,
						estimatedScrapQuantity
					);
					setTotalRequiredLength(result.totalInchesNeeded);
					setNumberOfBars(result.barsNeeded);
			} else {
				calculateRequiredLengths({
					material_cut_length: length,
					material_cut_quantity: cutQuantity,
					saw_kerf: sawKerf,
					required_trim_cut_length: trimCutLength,
				})
						.unwrap()
						.then((data) => setTotalRequiredLength(data.total_required_length));
				}
			}
		}
	}, [selectedShape, stockPreviewQuery.isSuccess]);

	return (
		<Modal
			open={visible}
			title={'Reserve Material'}
			maskClosable={false}
			closable={false}
			width={'calc(100vw - 50px)'}
			footer={
				<div className="flex flex-row justify-end">
					<Button type="primary" onClick={handleCancel}>
						Done
					</Button>
				</div>
			}
			onCancel={handleCancel}
			destroyOnClose={true}
			confirmLoading={loading}
			centered={true}
			style={{
				maxWidth: '2560px',
			}}
			styles={{
				body: {
					height: 'calc(100vh - 150px)',
					maxHeight: '1080px',
					overflowX: 'hidden',
					overflowY: 'auto',
				},
			}}
		>
			<Splitter className="gap-2">
				<Splitter.Panel>
					<div className="h-full flex flex-col justify-between overflow-x-hidden">
						<Tabs
							defaultActiveKey={isSwissProduction ? '2' : '1'}
							items={[
								{
									label: '3D File',
									key: '1',
									children: (
										<>
											{!props.lineItem?.step_file ? (
												<>
													<Alert
														showIcon
														type="warning"
														description="A 3D file has not been uploaded. Stock Preview will not be enabled."
													/>
												</>
											) : (
												<Spin
													indicator={<LoadingOutlined />}
													tip="Updating Stock Preview"
													spinning={
														stockPreviewQuery.isLoading ||
														stockPreviewQuery.isFetching ||
														isGeometryOpPending
													}
												>
													<div
														// className="border rounded mb-3"
														style={{ height: '640px', overflow: 'hidden' }}
													>
														{stockPreviewQuery.isLoading ? (
															<LoadingOutlined />
														) : (
															<ThreeDStockSizePreviewer
																partBufferGeometry={
																	stockPreviewQuery?.data?.part
																}
																stockBufferGeometry={
																	stockPreviewQuery?.data?.stock
																}
																jawsBufferGeometry={
																	isSwissProduction
																		? undefined
																		: stockPreviewQuery?.data?.jaws
																}
																updateMatrix={updateMatrix}
																partMatrix={
																	props?.lineItem?.part_transform_matrix
																}
																showPartTransformControls={true}
																handleResetPartRotation={
																	handleResetPartRotation
																}
																handleClickFaceForAlignment={
																	handleClickFaceForAlignment
																}
																handleClickFaceForXAxisAlignment={
																	handleClickFaceForXAxisAlignment
																}
																loading={
																	stockPreviewQuery?.isLoading ||
																	stockPreviewQuery.isFetching ||
																	isGeometryOpPending
																}
															/>
														)}
													</div>
												</Spin>
											)}
										</>
									),
								},
								{
									label: 'Drawing File',
									key: '2',
									children: props.lineItem.drawing_file ? (
										<div
											style={{
												position: 'relative',
												width: '100%',
												height: 0,
												paddingBottom: '77%',
											}}
										>
											<iframe
												src={`${props.lineItem.drawing_file}#toolbar=1&scrollbar=1`}
												style={{
													position: 'absolute',
													width: '100%',
													height: '100%',
													left: 0,
													top: 0,
												}}
											/>
										</div>
									) : (
										<>
											<Alert
												showIcon
												type="warning"
												description="A Drawing file has not been uploaded."
											/>
										</>
									),
								},
							]}
						/>

						<Row gutter={[16, 16]}>
							<Col span={4}>
								<Text strong>Order</Text>
								<br />
								<Text>{props.lineItem.order.name}</Text>
							</Col>
							<Col span={6}>
								<Text strong>Part</Text>
								<br />
								<Text>{props.lineItem.name}</Text>
							</Col>
							<Col span={4}>
								<Text strong>Material</Text>
								<br />
								<Text>{props.lineItem.material_name}</Text>
							</Col>
							<Col span={4}>
								<Text strong>Quantity</Text>
								<br />
								<Text>{props.lineItem.quantity}</Text>
							</Col>
							<Col span={6}>
								<Text strong>Part Bounding Box</Text>
								{stockPreviewQuery.isLoading ? (
									<div className="flex flex-row gap-1">
										<LoadingOutlined /> Calculating
									</div>
								) : (
									<div>{`${stockPreviewQuery?.data?.part_bbox?.height}" H. X ${stockPreviewQuery?.data?.part_bbox?.width}" W. X ${stockPreviewQuery?.data?.part_bbox?.length}" L`}</div>
								)}
							</Col>
						</Row>
					</div>
				</Splitter.Panel>
				<Splitter.Panel min={480} style={{ overflowX: 'hidden' }}>
					<Form
						form={form}
						layout="vertical"
						// onFinish={handleOk}
						requiredMark={false}
						style={{ paddingRight: '1rem' }}
						initialValues={{
							material_cut_length: null,
							material_cut_quantity: props.lineItem.quantity,
							estimated_scrap_quantity: Math.round(
								props.lineItem.quantity * 0.15
							),
							sawKerf: 0.07,
						}}
					>
						{showBuildingFilter && (
							<div
								style={{
									border: `1px solid ${selectedBuilding ? '#d9d9d9' : '#faad14'}`,
									borderRadius: 8,
									background: selectedBuilding ? '#fafafa' : '#fffbe6',
									padding: '12px 12px 10px',
									marginBottom: 16,
								}}
							>
								<Form.Item
									label={
										<span>
											<Text type="danger">*</Text> Building
										</span>
									}
									required={false}
									style={{ marginBottom: 4 }}
								>
									<Select
										placeholder="Select a building to view inventory materials"
										allowClear={!isBuildingLocked}
										disabled={isBuildingLocked}
										status={!selectedBuilding ? 'warning' : undefined}
										value={selectedBuilding}
										onChange={(value) => setSelectedBuilding(value ?? null)}
									>
										{buildings.map((b) => (
											<Select.Option key={b.id} value={b.id}>
												{b.name}
											</Select.Option>
										))}
									</Select>
								</Form.Item>
								{isBuildingLocked ? (
									<Text type="secondary" style={{ fontSize: 12 }}>
										This line item already has reservations from {lockedBuilding.name}. All reservations must be from the same building.
									</Text>
								) : (
									<Text type={selectedBuilding ? 'secondary' : 'warning'} style={{ fontSize: 12 }}>
										Required before inventory results are shown.
									</Text>
								)}
							</div>
						)}
						<Row gutter={[8, 0]}>
							<Col span={12}>
								<Form.Item label="Shape">
									<Tooltip
										open={!selectedShape}
										title="Select a stock shape to start an automatic search of the inventory"
										placement="left"
									>
										<Select
											placeholder="Select a shape"
											value={selectedShape?.id}
											allowClear={true}
											onClear={() => setSelectedShape(null)}
											onChange={handleShapeChange}
											options={shapesQuery?.data?.map((x) => {
												return { label: x.display_name, value: x.id };
											})}
										/>
									</Tooltip>
								</Form.Item>
							</Col>

							<Col span={12}>
								<Form.Item label="Category">
									<Select
										placeholder="Select a category"
										allowClear={true}
										onClear={() => setSelectedCategory(null)}
										options={categoriesQuery?.data?.map((x) => {
											return { label: x.display_name, value: x.id };
										})}
										value={selectedCategory}
										onChange={handleCategoryChange}
									/>
								</Form.Item>
							</Col>

							<Col span={24}>
								<Form.Item label="Types">
									<Select
										placeholder="Select types"
										showSearch={true}
										optionFilterProp="label"
										mode="multiple"
										value={selectedTypes}
										allowClear={true}
										onClear={() => setSelectedTypes([])}
										onChange={(value) => setSelectedTypes(value)}
										options={typesQuery?.data?.map((x) => {
											return { label: x.display_name, value: x.id };
										})}
										disabled={!selectedCategory}
									/>
								</Form.Item>
							</Col>
						</Row>
						<Row gutter={[8, 16]}>
							{selectedShape?.display_name === RECTANGLE_BAR ? (
								<Col span={24}>
									<Checkbox
										checked={allowRotatedStock}
										onChange={(e) => setAllowRotatedStock(e.target.checked)}
										disabled={!selectedWidth || !selectedHeight}
									>
										<div className="flex flex-row gap-1 items-center">
											Allow Rotated Stock
											<Tooltip title="Allow the stock to be rotated 90 degrees around the Y-axis. This increases the chance of finding a smaller and cheaper material but the stock orientation may not be suitable for robot loading. This checkbox will be unavailable unless both width and height are selected.">
												<QuestionCircleTwoTone />
											</Tooltip>
										</div>
									</Checkbox>
								</Col>
							) : null}
							{selectedShape?.display_name === RECTANGLE_BAR ||
								selectedShape?.display_name === ROUND_BAR ? (
								<Col span={24}>
									<>
										<div className="flex flex-col items-center mb-3">
											{getSuggestedInventoryMaterialBasedOnPartOrientationQuery.isLoading ||
												getSuggestedInventoryMaterialBasedOnPartOrientationQuery.isFetching ? (
												<div className="flex flex-row gap-2">
													<LoadingOutlined /> Searching the inventory for the
													best material
												</div>
											) : (
												<>
													{getSuggestedInventoryMaterialBasedOnPartOrientationQuery.isSuccess ? (
														<>
															{getSuggestedInventoryMaterialBasedOnPartOrientationQuery
																?.data?.material ? (
																<>
																	<div className="flex flex-col items-center gap-2">
																		<Text italic>
																			{selectedShape?.display_name ===
																				RECTANGLE_BAR
																				? `Suggested Size: ${getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data?.material?.height}" H. X ${getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data?.material?.width}" W. X ${getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data?.material_cut_length}" ${isSwissProduction ? 'Part Length' : 'Cut Length'}`
																				: selectedShape?.display_name ===
																					ROUND_BAR
																					? `Suggested Size: ${getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data?.material?.outer_diameter}" OD. X ${getSuggestedInventoryMaterialBasedOnPartOrientationQuery?.data?.material_cut_length}" ${isSwissProduction ? 'Part Length' : 'Cut Length'}`
																					: null}
																		</Text>
																	</div>
																</>
															) : (
																<Alert
																	type="info"
																	showIcon={true}
																	className="w-full text-bold"
																	message="No suitable inventory material found based on stock shape and type"
																	action={
																		<Button
																			type="text"
																			ghost
																			icon={<ReloadOutlined />}
																			onClick={
																				getSuggestedInventoryMaterialBasedOnPartOrientationQuery.refetch
																			}
																		>
																			Search Again
																		</Button>
																	}
																/>
															)}
														</>
													) : null}
												</>
											)}
										</div>
									</>
								</Col>
							) : null}

							{(selectedShape?.display_name === RECTANGLE_BAR ||
								selectedShape?.display_name === RECTANGLE_TUBE ||
								selectedShape?.display_name === PLATE ||
								selectedShape?.display_name === ANGLE ||
								selectedShape?.display_name === CHANNEL ||
								selectedShape?.display_name === HEX_BAR) && (
									<Col span={8}>
										<Form.Item label="Height">
											<Select
												placeholder="Select a height"
												showSearch={true}
												optionFilterProp="label"
												value={selectedHeight}
												allowClear={true}
												onClear={() => {
													setSelectedHeight(null);
													setSelectedWidth(null);
													setSelectedWallThickness(null);
												}}
												onChange={(value) => handleChangeSelectedHeight(value)}
												options={uniqueHeightsQuery?.data?.map((x) => {
													return {
														value: x,
														label: x,
													};
												})}
												disabled={!selectedCategory}
											/>
										</Form.Item>
									</Col>
								)}

							{(selectedShape?.display_name === RECTANGLE_BAR ||
								selectedShape?.display_name === RECTANGLE_TUBE ||
								selectedShape?.display_name === PLATE ||
								selectedShape?.display_name === ANGLE ||
								selectedShape?.display_name === CHANNEL) && (
									<Col span={8}>
										<Form.Item label="Width">
											<Select
												placeholder="Select a width"
												showSearch={true}
												optionFilterProp="label"
												value={selectedWidth}
												allowClear={true}
												onClear={() => {
													setSelectedWidth(null);
													setSelectedWallThickness(null);
												}}
												onChange={(value) => handleChangeSelectedWidth(value)}
												options={uniqueWidthsQuery?.data?.map((x) => {
													return { value: x, label: x };
												})}
												disabled={
													!selectedCategory ||
													uniqueWidthsQuery.isLoading ||
													uniqueWidthsQuery.isFetching
												}
												loading={
													uniqueWidthsQuery.isLoading ||
													uniqueWidthsQuery.isFetching
												}
											/>
										</Form.Item>
									</Col>
								)}

							{(selectedShape?.display_name === RECTANGLE_TUBE ||
								selectedShape?.display_name === ANGLE ||
								selectedShape?.display_name === CHANNEL) && (
									<Col span={8}>
										<Form.Item label="Wall Thickness">
											<Select
												placeholder="Select a wall thickness"
												showSearch={true}
												optionFilterProp="label"
												value={selectedWallThickness}
												onChange={(value) => setSelectedWallThickness(value)}
												options={uniqueWallThicknessesQuery?.data?.map((x) => {
													return { value: x, label: x };
												})}
												disabled={!selectedCategory}
											/>
										</Form.Item>
									</Col>
								)}

							{selectedShape?.display_name === ROUND_BAR ||
								selectedShape?.display_name === ROUND_TUBE ? (
								<Col span={8}>
									<Form.Item label="Outer Diameter">
										<Select
											placeholder="Select a diameter"
											showSearch={true}
											optionFilterProp="label"
											value={selectedOuterDiameter}
											allowClear={true}
											onClear={() => {
												setSelectedOuterDiameter(null);
												setSelectedInnerDiameter(null);
											}}
											onChange={(value) => handleChangeOuterDiameter(value)}
											options={uniqueOuterDiametersQuery?.data?.map((x) => {
												return {
													value: x,
													label: x,
												};
											})}
											disabled={!selectedCategory}
										/>
									</Form.Item>
								</Col>
							) : null}

							{selectedShape?.display_name === ROUND_TUBE && (
								<Col span={8}>
									<Form.Item label="Inner Diameter">
										<Select
											placeholder="Select an inner diameter"
											showSearch={true}
											optionFilterProp="label"
											value={selectedInnerDiameter}
											onClear={() => {
												setSelectedInnerDiameter(null);
											}}
											onChange={(value) => handleChangeInnerDiameter(value)}
											options={uniqueInnerDiametersQuery?.data?.map((x) => {
												return {
													value: x,
													label: x,
												};
											})}
											disabled={!selectedCategory}
										/>
									</Form.Item>
								</Col>
							)}
							<Col span={8}>
								<Form.Item
									label={
										<div className="flex items-center gap-2">
											<span>
												{isSwissProduction ? 'Part Length' : 'Cut Length'}
											</span>
											{isSwissProduction && autoCalculatedPartLength && (
												<Button
													type="link"
													size="small"
													onClick={handleResetPartLength}
													disabled={
														materialCutLength === autoCalculatedPartLength ||
														Number(materialCutLength) ===
														Number(autoCalculatedPartLength)
													}
													style={{
														padding: 0,
														height: 'auto',
														fontSize: '14px',
													}}
												>
													Reset
												</Button>
											)}
										</div>
									}
								>
									<InputNumber
										placeholder={isSwissProduction ? 'Part Length' : 'Cut Length'}
										value={materialCutLength}
										step={0.125}
										onStep={(value) => handleMaterialCutLengthChange(value)}
										onPressEnter={(e) => {
											handleMaterialCutLengthChange(
												(e.target as HTMLInputElement).value
											);
										}}
										onBlur={(e) =>
											handleMaterialCutLengthChange(e.target.value)
										}
										style={{ width: '100%' }}
										suffix="inches"
									/>
								</Form.Item>
							</Col>
						</Row>

					<Row gutter={[8, 0]}>
						<Col span={isSwissProduction ? 8 : 6}>
							<Form.Item
								label={isSwissProduction ? 'Cut Off Length' : 'Saw Kerf'}
								initialValue={sawKerf}
							>
								<InputNumber
									placeholder={
										isSwissProduction ? 'Cut Off Length' : 'Saw Kerf'
									}
									step={0.01}
									min={0}
									value={sawKerf}
									onStep={(value) => handleSawKerfChange(value)}
									onBlur={(e) => handleSawKerfChange(Number(e.target.value))}
									style={{ width: '100%' }}
								/>
							</Form.Item>
						</Col>
						{!isSwissProduction && (
							<Col span={6}>
								<Form.Item label="Trim Cut">
									<InputNumber
										placeholder="Trim Cut"
										step={0.125}
										min={0}
										value={trimCutLength}
										onChange={(value) => {
											if (value !== null) setTrimCutLength(value);
										}}
										onStep={(value) => {
											setTrimCutLength(value);
											calculateRequiredLengths({
												material_cut_length: Number(materialCutLength),
												saw_kerf: sawKerf,
												material_cut_quantity: cutQuantity,
												required_trim_cut_length: value,
											})
												.unwrap()
												.then((data) => setTotalRequiredLength(data.total_required_length));
										}}
										onBlur={(e) => {
											const value = Number(e.target.value);
											setTrimCutLength(value);
											calculateRequiredLengths({
												material_cut_length: Number(materialCutLength),
												saw_kerf: sawKerf,
												material_cut_quantity: cutQuantity,
												required_trim_cut_length: value,
											})
												.unwrap()
												.then((data) => setTotalRequiredLength(data.total_required_length));
										}}
										style={{ width: '100%' }}
										suffix="inches"
									/>
								</Form.Item>
							</Col>
						)}
						<Col span={isSwissProduction ? 8 : 6}>
							<Form.Item
								label={isSwissProduction ? 'Parts' : 'Cut Quantity'}
							>
								<InputNumber
									placeholder={isSwissProduction ? 'Parts' : 'Cut Quantity'}
									step={1}
									min={1}
									value={cutQuantity}
									onStep={(value) => handleMaterialCutQuantityChange(value)}
									onBlur={(e) =>
										handleMaterialCutQuantityChange(Number(e.target.value))
									}
									style={{ width: '100%' }}
								/>
							</Form.Item>
						</Col>
						{isSwissProduction && (
							<Col span={8}>
								<Form.Item label="Estimated Scrap Quantity">
									<InputNumber
										placeholder="Estimated Scrap Quantity"
										step={1}
										min={0}
										value={estimatedScrapQuantity}
										onStep={(value) => handleMaterialScrapQuantityChange(value)}
										onBlur={(e) =>
											handleMaterialScrapQuantityChange(Number(e.target.value))
										}
										style={{ width: '100%' }}
									/>
								</Form.Item>
							</Col>
						)}

							<Col span={24}>
								<Form.Item name="total_required_length" hidden={true} />

								{isSwissProduction ? (
									<>
										{totalRequiredLength !== 0 ? (
											<div className="flex flex-col items-end mb-3">
												<Title level={5}>
													Total Inches Needed:{' '}
													<span className="text-lg p-1 rounded">
														{`${totalRequiredLength.toFixed(2)}"`}
													</span>
												</Title>
											</div>
										) : null}
									</>
								) : totalRequiredLength !== 0 ? (
									<div className="flex flex-col items-end mb-5">
										<Title level={5}>
											Total Required Length:{' '}
											<span className="text-lg">
												{`${totalRequiredLength}"`}
											</span>
										</Title>
									</div>
								) : null}
							</Col>
						</Row>

						{isSwissProduction && (
							<Row gutter={[8, 0]}>
								<Col span={8}>
									<Form.Item
										label="Number of Bars Needed"
									>
										<InputNumber
											placeholder="Number of Bars Needed"
											step={1}
											min={1}
											value={numberOfBars}
											onChange={(value) => {
												if (value) {
													setNumberOfBars(value);
												}
											}}
											style={{ width: '100%' }}
										/>
									</Form.Item>
								</Col>
							</Row>
						)}
					</Form>

					{selectedMaterial ? (
						<Alert
							style={{
								padding: '0.5rem 0.5rem 0.5rem 0.75rem',
								alignItems: 'center',
								marginBottom: '1rem',
							}}
							description={
								<>
									<Text strong>Selected Material</Text>
									<br /> {selectedMaterial?.display_name}
								</>
							}
							action={
								<div className="flex flex-row gap-1">
									<Button
										onClick={async () => {
											setSelectedMaterial(null);
										}}
									>
										Clear Selection
									</Button>
								</div>
							}
						/>
					) : null}

					<div className="flex items-baseline gap-1">
						<Title level={5}>Select an Inventory Material</Title>
					</div>
					{showBuildingFilter && !selectedBuilding ? (
						<Empty description="Please select a building to view inventory materials" />
					) : (
						<MaterialInventoryTable
							data={
								materialsQuery?.data?.results ? materialsQuery.data.results : []
							}
							lineItem={props.lineItem}
						material_cut_length={materialCutLength ?? null}
						saw_kerf={sawKerf}
						material_cut_quantity={
							isSwissProduction ? cutQuantity + estimatedScrapQuantity : cutQuantity
						}
						total_required_length={totalRequiredLength}
						required_trim_cut_length={trimCutLength}
							isLoading={materialsQuery.isLoading || materialsQuery.isFetching}
							selectedShape={
								selectedShape ? selectedShape?.display_name : RECTANGLE_BAR
							}
							setSelectedMaterial={(selectedMaterial) => {
								if (props.onMaterialSelect) {
									props.onMaterialSelect(selectedMaterial, materialsQuery.refetch);
								}
							}}
							tableProps={{
								size: 'middle',
								expandable: true,
								scrollY: '720px',
								empty: (
									<Empty description="No material matching the search critera found in inventory">
										<Button
											type="primary"
											onClick={() => {
												onClose();
												dispatch(showMaterialRequestModal());
											}}
										>
											Request Material
										</Button>
									</Empty>
								),
							}}
							showActionsColumn={true}
							selectedPreviewMaterial={selectedMaterial?.id}
							onChangePreviewMaterial={handleChangePreviewMaterial}
							lockedBuildingId={lockedBuilding?.id ?? null}
							refetchData={() => {
								materialsQuery.refetch();
								props.refetchLineItem();
							}}
						/>
					)}
				</Splitter.Panel>
			</Splitter>
		</Modal>
	);
};

export default MaterialSelectionModal2;
